import React, { useState, useMemo, useEffect } from "react";

// --- SVG & UI HELPER COMPONENTS (Defined outside the main component) ---

const Icon = ({ path, className = "w-6 h-6" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d={path} />
  </svg>
);

const FluidBagIcon = () => <Icon path="M12 2C8.13 2 5 5.13 5 9c0 1.62.44 3.13 1.2 4.44L5 22h14l-1.2-8.56C18.56 12.13 19 10.62 19 9c0-3.87-3.13-7-7-7zm0 2c2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5 2.24-5 5-5z" />;
const BeakerIcon = () => <Icon path="M3 3h18v2H3V3zm2 3h14v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6zm2 2v2h10V8H7zm0 4v2h10v-2H7zm0 4v2h10v-2H7z" />;
const WarningIcon = () => <Icon path="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2V7h2v7z" className="w-5 h-5 inline-block ml-2 text-orange-500" />;
const InfoIcon = ({className = "w-6 h-6"}) => <Icon path="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" className={className} />;


const ChevronDownIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const Collapsible = ({ title, children, isOpen, onToggle, className = "" }) => (
  <div className={`border-t border-black border-opacity-10 mt-4 pt-3 ${className}`}>
    <button onClick={onToggle} className="w-full flex justify-between items-center text-sm font-medium text-gray-700 hover:bg-gray-50 p-1 rounded-md transition-colors duration-200">
      <span>{title}</span>
      <ChevronDownIcon className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
    </button>
    {isOpen && <div className="mt-3">{children}</div>}
  </div>
);

const Gauge = ({ value }) => {
  const percentage = Math.min(Math.max(value, 0), 1) * 100;
  const color = percentage > 25 ? '#ef4444' : percentage > 20 ? '#f97316' : '#22c55e';

  return (
    <div className="relative w-24 h-12">
      <svg viewBox="0 0 100 50" className="w-full h-full">
        <path d="M10 50 A 40 40 0 0 1 90 50" stroke="#e5e7eb" strokeWidth="8" fill="none" />
        <path d="M10 50 A 40 40 0 0 1 90 50" stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={`${percentage * 1.256}, 125.6`}
        />
      </svg>
      <div className="absolute bottom-0 w-full text-center text-lg font-bold text-gray-700">
        {Math.round(percentage)}%
      </div>
    </div>
  );
};

const BaseSchematic = ({ highlight = 'none', showYin = false, highlightAllBags = false }) => (
    <svg viewBox="0 0 300 220" className="w-full h-auto" aria-labelledby="title" role="img">
        <title id="title">CRRT Circuit Schematic</title>
        <defs>
            <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#3b82f6" />
            </marker>
            <style>{`
                .blood-line { stroke: #ef4444; stroke-width: 2.5; fill: none; }
                .fluid-line { stroke: #3b82f6; stroke-width: 2; fill: none; }
                .dialysate-line { stroke: #22c55e; stroke-width: 2; fill: none; }
                .effluent-line { stroke: #9ca3af; stroke-width: 2; fill: none; }
                .label { font-family: sans-serif; font-size: 10px; fill: #4b5563; }
                .bag-label { font-family: sans-serif; font-size: 9px; fill: #1f2937; }
                .patient-label { font-family: sans-serif; font-size: 12px; font-weight: bold; fill: #111827;}
                .bag { fill: #f9fafb; stroke: #d1d5db; stroke-width: 1; }

                .flow-animation {
                    stroke-dasharray: 4 4;
                    animation: flow 2s linear infinite;
                }
                @keyframes flow { to { stroke-dashoffset: -8; } }

                .highlight {
                    stroke: #fef08a; stroke-width: 6;
                    filter: drop-shadow(0 0 4px #fef08a);
                    animation: pulse-glow 1.5s infinite ease-in-out;
                }
                .highlight-bag {
                    fill: #fef9c3; stroke: #facc15; stroke-width: 1.5;
                    filter: drop-shadow(0 0 4px #fef08a);
                    animation: pulse-glow-bag 1.5s infinite ease-in-out;
                }
                @keyframes pulse-glow { 0%, 100% { stroke-width: 6; filter: drop-shadow(0 0 4px #fef08a); } 50% { stroke-width: 7; filter: drop-shadow(0 0 8px #facc15); } }
                @keyframes pulse-glow-bag { 0%, 100% { filter: drop-shadow(0 0 4px #fef08a); } 50% { filter: drop-shadow(0 0 8px #facc15); } }
            `}</style>
        </defs>
        <path d="M50,115 C 80,115 80,115 80,115" className="blood-line flow-animation" />
        <path d="M105,115 H 150" className="blood-line flow-animation" />
        <path d="M170,115 H 225" className="blood-line flow-animation" />
        <path d="M225,115 C 250,115 250,115 280,115" className="blood-line flow-animation" />
        
        <text x="10" y="120" className="patient-label">Patient</text>
        <text x="55" y="108" className="label">Access</text>
        <circle cx="95" cy="115" r="10" fill="#f9fafb" stroke="#6b7280" strokeWidth="1.5" />
        <circle cx="95" cy="115" r="2" fill="#6b7280" />
        <text x="90" y="135" className="label">Qb</text>

        <path d="M125,50 V 95" className={`fluid-line flow-animation ${highlightAllBags ? 'highlight' : ''}`} />
        <path d="M125,95 H 115" className="fluid-line" />
        <rect x="115" y="30" width="20" height="20" rx="3" className={`bag ${highlightAllBags ? 'highlight-bag' : ''}`} />
        <text x="117" y="44" className="bag-label">Pre</text>

        <rect x="150" y="85" width="20" height="60" fill="#fff" stroke="#374151" strokeWidth="1.5" />
        <path d="M152,95 h16 M152,105 h16 M152,115 h16 M152,125 h16" stroke="#d1d5db" strokeWidth="0.5" />
        <text x="148" y="80" className="label">Filter</text>

        <path d="M160,145 V 180 H 190" className="effluent-line flow-animation" />
        <rect x="180" y="180" width="20" height="20" rx="3" className="bag" />
        <text x="181" y="194" className="bag-label">Effl.</text>

        <path d="M190,50 V 85" className={`dialysate-line flow-animation ${highlightAllBags ? 'highlight' : ''}`} />
        <path d="M190,85 H 170" className="dialysate-line" />
        <rect x="180" y="30" width="20" height="20" rx="3" className={`bag ${highlightAllBags ? 'highlight-bag' : ''}`} />
        <text x="181" y="44" className="bag-label">Dial.</text>

        <path d="M215,50 V 95" className={`fluid-line flow-animation ${highlight === 'post' || highlightAllBags ? 'highlight' : ''}`} />
        <path d="M215,95 H 205" className="fluid-line" />
        <rect x="205" y="30" width="20" height="20" rx="3" className={`bag ${highlight === 'post' || highlightAllBags ? 'highlight-bag' : ''}`} />
        <text x="207" y="44" className="bag-label">Post</text>

        {showYin && (
            <>
                <path d="M255,50 V 150 H 40" className={`fluid-line flow-animation ${highlight === 'yin' ? 'highlight' : ''}`} markerEnd="url(#arrow)" />
                <rect x="245" y="30" width="20" height="20" rx="3" className={`bag ${highlight === 'yin' ? 'highlight-bag' : ''}`} />
                <text x="242" y="44" className="bag-label">Extra IV</text>
            </>
        )}
        <text x="240" y="108" className="label">Return</text>
    </svg>
);

const InputField = ({ label, unit, labelClassName = "", inputClassName = "", ...props }) => (
  <div className="relative">
    <label className={`block text-sm font-medium text-gray-600 ${labelClassName}`}>{label}</label>
    <input 
      type="number" 
      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#4F2683] focus:ring-2 focus:ring-[#4F2683] focus:ring-opacity-50 text-sm pr-16 transition ${inputClassName}`} 
      {...props}
    />
    {unit && <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-500 pt-6">{unit}</span>}
  </div>
);

const ToggleSwitch = ({ isEnabled, onToggle, label }) => (
    <div className="flex items-center justify-between">
        {label && <span className="text-sm font-medium text-gray-600">{label}</span>}
        <button onClick={onToggle} className={`relative inline-flex items-center h-7 rounded-full w-12 transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4F2683] hover:scale-105 transform ${isEnabled ? 'bg-[#4F2683]' : 'bg-gray-300'}`}>
            <span className={`inline-block w-5 h-5 transform bg-white rounded-full transition-transform duration-300 ease-in-out ${isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
    </div>
);

// --- MAIN CALCULATOR COMPONENT ---
export default function App() {
  // --- STATE MANAGEMENT ---
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [methodInfo, setMethodInfo] = useState({ isOpen: false, title: '', content: [] });
  const [showCalc1, setShowCalc1] = useState(false);
  const [showCalc2, setShowCalc2] = useState(false);
  const [showCalc3, setShowCalc3] = useState(false);
  const [showSchematic1, setShowSchematic1] = useState(true);
  const [showSchematic2, setShowSchematic2] = useState(true);
  const [showSchematic3, setShowSchematic3] = useState(true);
  const [m1Error, setM1Error] = useState(null);
  const [showM1ConflictModal, setShowM1ConflictModal] = useState(false);
  const [m1ConflictData, setM1ConflictData] = useState(null);

  const [patient, setPatient] = useState({
    weight: "4", height: "55", Hct: "30", Na_patient: "120", Qb: "50",
    mode: 'clearance', dose: "35", clearance: "2.0", targetNa: "126"
  });
  const [citrate, setCitrate] = useState({ active: false, rate: "0", sodium: "224" });
  const [minimalPFR, setMinimalPFR] = useState({ active: true, rate: "100" });
  const [targetFF, setTargetFF] = useState("20");

  const [m1, setM1] = useState({
    modality: 'CVVHDF', Qr_pre: "0", Qr_post: "0", Qd: "0", UF: "0", Na_r_post: "140", Na_standard: "140"
  });
  const [isM1Optimized, setIsM1Optimized] = useState(false);

  const [m2, setM2] = useState({
    modality: 'CVVHDF', Qr_pre: "0", Qr_post: "0", Qd: "0", UF: "0", Na_yin: "0", yin_rate: "0", Na_standard: "140"
  });
  const [isM2Optimized, setIsM2Optimized] = useState(false);

  const [m3, setM3] = useState({ 
      bagVolume: "5000", 
      bagNa: "140", 
      includeCitrate: false, 
  });

  // --- EVENT HANDLERS ---
  const handlePatient = e => {
    const { name, value } = e.target;
    setPatient(p => ({ ...p, [name]: value }));
    setIsM1Optimized(false);
    setIsM2Optimized(false);
  }
  const handleCitrate = e => {
    setCitrate(p => ({ ...p, [e.target.name]: e.target.value }));
    setIsM1Optimized(false);
    setIsM2Optimized(false);
  }
  const handleMinimalPFR = e => {
    setMinimalPFR(p => ({ ...p, [e.target.name]: e.target.value }));
    setIsM1Optimized(false);
    setIsM2Optimized(false);
  }
   const handleTargetFF = e => {
    setTargetFF(e.target.value);
    setIsM1Optimized(false);
  }
  const handleM1 = e => {
    setM1(p => ({ ...p, [e.target.name]: e.target.value }));
    setIsM1Optimized(false);
  };
   const handleM2 = e => {
    setM2(p => ({ ...p, [e.target.name]: e.target.value }));
    setIsM2Optimized(false);
  };
  const handleM3 = e => setM3(p => ({ ...p, [e.target.name]: e.target.value }));
  const num = v => parseFloat(v) || 0;

  const handleShowMethodInfo = (title, content) => {
    setMethodInfo({ isOpen: true, title, content });
  };

  // --- CALCULATIONS (useMemo for optimization) ---
  const plasmaFlow = useMemo(() => num(patient.Qb) * 60 * (1 - num(patient.Hct) / 100), [patient.Qb, patient.Hct]);
  const bsa = useMemo(() => {
    const h = num(patient.height), w = num(patient.weight);
    return h && w ? Math.sqrt((h * w) / 3600) : 0;
  }, [patient.height, patient.weight]);

  const effluent = useMemo(() => {
    const w = num(patient.weight);
    return patient.mode === 'dose'
      ? num(patient.dose) * w
      : num(patient.clearance) * (bsa / 1.73) * 1000;
  }, [patient.mode, patient.dose, patient.clearance, patient.weight, bsa]);

  // --- AUTO-CALCULATION LOGIC ---
 
  // Effect to automatically update Target Na based on Serum Na
  useEffect(() => {
    const serumNa = num(patient.Na_patient);
    if (serumNa > 0) {
        let newTargetNa = serumNa;
        if (serumNa < 135) {
            newTargetNa = serumNa + 6;
        } else if (serumNa > 145) {
            newTargetNa = serumNa - 6;
        }
        setPatient(p => ({...p, targetNa: String(Math.round(newTargetNa))}));
    }
  }, [patient.Na_patient]);


  // Effect to set default UF rate based on patient weight
  useEffect(() => {
    const newUfRate = Math.round(num(patient.weight) * 1);
    setM1(p => ({ ...p, UF: String(newUfRate) }));
    setM2(p => ({ ...p, UF: String(newUfRate) }));
  }, [patient.weight]);
 
  // Effect to set smart defaults for fluids based on target sodium
  useEffect(() => {
    const targetNaNum = num(patient.targetNa);
    if (targetNaNum < 130) {
      setM1(p => ({ ...p, Na_r_post: "77" })); // 0.45% NS
    } else if (targetNaNum > 150) {
      setM1(p => ({ ...p, Na_r_post: "513" })); // 3% HTS
    } else {
      setM1(p => ({ ...p, Na_r_post: "140" })); // Standard
    }
  }, [patient.targetNa]);


  // Effect to handle citrate toggle
  useEffect(() => {
    if (citrate.active) {
      setCitrate(c => ({...c, rate: String(Math.round(num(patient.Qb) * 1.5))}));
      setM1(p => ({...p, Qr_pre: "0"}));
      setM2(p => ({...p, Qr_pre: "0"}));
    } else {
      setCitrate(c => ({...c, rate: "0"}));
    }
    setIsM1Optimized(false);
    setIsM2Optimized(false);
  }, [citrate.active, patient.Qb]);

  // Effect for Method 1 Optimization
  useEffect(() => {
    if (!isM1Optimized) return;
    setM1Error(null);

    const safeFFTarget = num(targetFF) / 100;

    const calculatePrescription = (currentClearance) => {
        const Qc = citrate.active ? num(citrate.rate) : 0;
        const TargetNa = num(patient.targetNa);
        const Na_r_post = num(m1.Na_r_post);
        const Na_d = num(m1.Na_standard);
        const Na_c = num(citrate.sodium);
        const Na_pre = num(m1.Na_standard);
        const UF = num(m1.UF);

        let Qr_pre = 0, Qr_post = 0, Qd = 0;
        const V_total = currentClearance;

        if (m1.modality === 'CVVH' && !citrate.active) {
            // CVVH logic (only when citrate is OFF)
            const denom = Na_r_post - Na_pre;
            if (denom !== 0) {
                Qr_post = (V_total * (TargetNa - Na_pre)) / denom;
                Qr_pre = V_total - Qr_post;
            } else { return null; }
        } else if (m1.modality === 'CVVHDF' && !citrate.active) {
            // New 50/50 split logic for CVVHDF when citrate is OFF
            const denom = Na_pre - 2 * Na_r_post + Na_d;
            if (denom !== 0) {
                Qr_pre = V_total * (TargetNa - Na_r_post) / denom;
                Qd = Qr_pre;
                Qr_post = V_total - 2 * Qr_pre;
            } else { return null; }
        } else {
            // Original CVVHDF logic (for CVVHDF with citrate ON, or CVVH with citrate ON)
            Qr_pre = 0;
            const denom = Na_r_post - Na_d;
            if (denom !== 0) {
                Qr_post = (V_total * (TargetNa - Na_d) + Qc * (Na_d - Na_c)) / denom;
                Qd = V_total - Qc - Qr_post;
            } else { return null; }
        }

        const minPFR = minimalPFR.active ? num(minimalPFR.rate) : 0;
        let status = 'ok';
        if (minimalPFR.active && Qr_post < minPFR) {
            // Required post-filter flow is below the minimum. Post flow is fixed by the
            // target-Na and fluid choice, so hold it at the minimum and re-solve the
            // standard-Na flow to keep the Na target, letting clearance rise (shown to
            // the user). If the target can't be held with this fluid, fall back to
            // preserving clearance and flag the Na miss.
            Qr_post = minPFR;
            if (citrate.active) {
                const denom = Na_d - TargetNa;
                const Qd_hold = denom !== 0 ? (Qr_post * (TargetNa - Na_r_post) + Qc * (TargetNa - Na_c)) / denom : -1;
                if (Qd_hold < 0) { status = 'target_miss'; Qr_pre = 0; Qd = Math.max(V_total - Qr_post - Qc, 0); }
                else { status = 'clearance_high'; Qr_pre = 0; Qd = Qd_hold; }
            } else if (m1.modality === 'CVVH') {
                const denom = Na_pre - TargetNa;
                const S = denom !== 0 ? (Qr_post * (TargetNa - Na_r_post)) / denom : -1;
                if (S < 0) { status = 'target_miss'; Qr_pre = Math.max(V_total - Qr_post, 0); Qd = 0; }
                else { status = 'clearance_high'; Qr_pre = S; Qd = 0; }
            } else { // CVVHDF citrate-off
                const denom = Na_d - TargetNa;
                const S = denom !== 0 ? (Qr_post * (TargetNa - Na_r_post)) / denom : -1;
                if (S < 0) { status = 'target_miss'; const std = Math.max(V_total - Qr_post, 0); Qr_pre = std / 2; Qd = std / 2; }
                else { status = 'clearance_high'; Qr_pre = S / 2; Qd = S / 2; }
            }
        }
       
        if (Qr_pre < 0) Qr_pre = 0;
        if (Qr_post < 0) Qr_post = 0;
        if (Qd < 0) Qd = 0;
       
        const V_total_final = Qr_pre + Qr_post + Qd + Qc;
        const FF = (UF + Qr_pre + Qr_post + Qc) / (plasmaFlow + Qr_pre + Qc);

        return { Qr_pre:String(Math.round(Qr_pre)), Qr_post:String(Math.round(Qr_post)), Qd:String(Math.round(Qd)), UF:String(Math.round(UF)), FF, V_total_final, status };
    };

    const idealPrescription = calculatePrescription(effluent);

    if (!idealPrescription) {
        setM1Error("Calculation failed. This can happen if Post-filter Na⁺ and Standard CRRT fluid Na⁺ are the same.");
        setIsM1Optimized(false);
        return;
    }

    // Post-filter flow had to be clamped to the minimum. Lowering clearance only
    // lowers post flow further, so the FF-reduction loop can't help here: report
    // the trade-off directly and keep the populated prescription on screen.
    if (idealPrescription.status === 'clearance_high') {
        setM1(p => ({ ...p, ...idealPrescription }));
        const clr = patient.mode === 'clearance'
            ? (bsa > 0 ? (idealPrescription.V_total_final / 1000) / (bsa / 1.73) : 0)
            : (num(patient.weight) > 0 ? idealPrescription.V_total_final / num(patient.weight) : 0);
        setM1Error(`To keep post-filter flow ≥ ${minimalPFR.rate} mL/h, clearance must rise to ~${clr.toFixed(1)} ${targetClearanceUnits}. The Na⁺ target is met. Consider Method 2 or 3, or a lower minimum post-filter rate.`);
        return;
    }
    if (idealPrescription.status === 'target_miss') {
        setM1(p => ({ ...p, ...idealPrescription }));
        setM1Error(`Target Na⁺ can't be reached with this post-filter fluid. The outlet shown is the closest at the prescribed clearance. Choose a post-filter Na⁺ on the correct side of the target, or use Method 2 or 3.`);
        return;
    }

    if (idealPrescription.FF < safeFFTarget) {
        setM1(p => ({ ...p, ...idealPrescription }));
        const clearanceDeviation = Math.abs(idealPrescription.V_total_final - effluent) / effluent;
        if (clearanceDeviation > 0.01) {
            setM1Error(`Warning: Either Filtration Fraction or Clearance limit may be violated`);
        }
    } else {
        let safePrescription = null;
        let currentClearance = effluent;
        for (let i = 0; i < 20; i++) {
            currentClearance *= 0.95;
            const tempPrescription = calculatePrescription(currentClearance);
            if (tempPrescription && tempPrescription.FF < safeFFTarget) {
                safePrescription = tempPrescription;
                break;
            }
        }

        if (safePrescription) {
            setM1ConflictData({
                ideal: idealPrescription,
                safe: safePrescription,
                targetFF: targetFF
            });
            setShowM1ConflictModal(true);
        } else {
            setM1(p => ({ ...p, ...idealPrescription }));
            setM1Error(`Warning: FF is high (${(idealPrescription.FF * 100).toFixed(0)}%). Unable to find a setting with FF < ${targetFF}%.`);
        }
    }
  }, [isM1Optimized, m1.modality, m1.Na_r_post, m1.Na_standard, citrate, effluent, patient, minimalPFR, m1.UF, plasmaFlow, targetFF]);


  // Effect for Method 2 Optimization
  useEffect(() => {
    if (!isM2Optimized) return;

    const Qc = citrate.active ? num(citrate.rate) : 0;
    const V_total_target = effluent;
    const minPFR = minimalPFR.active ? num(minimalPFR.rate) : 0;

    const TargetNa = num(patient.targetNa);
    const Na_yin = num(m2.Na_yin);
    const Na_standard_fluid = num(m2.Na_standard);
    const Na_c = num(citrate.sodium);
   
    let pre = 0, post = 0, dial = 0, yin = 0;

    if (m2.modality === 'CVVH') {
        const Na_standard = Na_standard_fluid;
        const denominator = Na_yin - Na_standard;
        if (denominator !== 0) {
            yin = (V_total_target * (TargetNa - Na_standard) - (Qc * (Na_c - Na_standard))) / denominator;
            yin = Math.max(0, yin);
        }
       
        const remainingVolume = V_total_target - yin - Qc;
       
        if (citrate.active) {
            pre = 0;
            post = remainingVolume;
        } else {
            if (minimalPFR.active) {
                post = Math.min(minPFR, remainingVolume > 0 ? remainingVolume : 0);
            }
            pre = remainingVolume - post;
        }

    } else { // CVVHDF
        const Na_other = Na_standard_fluid;
        const denominator = Na_yin - Na_other;
        if (denominator !== 0) {
            yin = (V_total_target * (TargetNa - Na_other) - (Qc * (Na_c - Na_other))) / denominator;
            yin = Math.max(0, yin);
        }
       
        const remainingVolume = V_total_target - yin - Qc;
        post = 0;
        if (minimalPFR.active) {
            post = Math.min(minPFR, remainingVolume > 0 ? remainingVolume : 0);
        }
        const volumeForSplit = remainingVolume - post;
        if (citrate.active) {
            pre = 0;
            dial = volumeForSplit;
        } else {
            pre = volumeForSplit / 2;
            dial = volumeForSplit / 2;
        }
    }
   
    if (pre < 0) pre = 0;
    if (post < 0) post = 0;
    if (dial < 0) dial = 0;
    if (yin < 0) yin = 0;

    setM2(p => ({
      ...p,
      yin_rate: String(Math.round(yin)),
      Qr_post: String(Math.round(post)),
      Qr_pre: String(Math.round(pre)),
      Qd: String(Math.round(dial)),
    }));

  }, [isM2Optimized, m2.modality, citrate, m2.Na_yin, m2.Na_standard, effluent, patient.targetNa, minimalPFR]);


  // Method 1 Calcs
  const Qc1 = citrate.active ? num(citrate.rate) : 0;
  const totalVol1 = useMemo(() => num(m1.Qr_pre) + num(m1.Qr_post) + (m1.modality === 'CVVHDF' ? num(m1.Qd) : 0) + Qc1, [m1, Qc1]);
  const na_pre1 = useMemo(() => num(m1.Qr_pre) * num(m1.Na_standard), [m1.Qr_pre, m1.Na_standard]);
  const na_post1 = useMemo(() => num(m1.Qr_post) * num(m1.Na_r_post), [m1]);
  const na_d1 = useMemo(() => m1.modality === 'CVVHDF' ? num(m1.Qd) * num(m1.Na_standard) : 0, [m1]);
  const na_c1 = useMemo(() => Qc1 * num(citrate.sodium), [Qc1, citrate.sodium]);
  const totalNa1 = useMemo(() => na_pre1 + na_post1 + na_d1 + na_c1, [na_pre1, na_post1, na_d1, na_c1]);
  const outletNa1 = useMemo(() => totalVol1 > 0 ? totalNa1 / totalVol1 : 0, [totalNa1, totalVol1]);
  const FF1 = useMemo(() => { const d = plasmaFlow + num(m1.Qr_pre) + Qc1; return d > 0 ? (num(m1.UF) + num(m1.Qr_pre) + num(m1.Qr_post) + Qc1) / d : 0; }, [m1, plasmaFlow, Qc1]);
  const prescribedClearance1 = useMemo(() => {
    if (patient.mode === 'clearance') return bsa > 0 ? (totalVol1 / 1000) / (bsa / 1.73) : 0;
    return num(patient.weight) > 0 ? totalVol1 / num(patient.weight) : 0;
  }, [totalVol1, patient.mode, bsa, patient.weight]);

  // Method 2 Calcs
  const Qc2 = citrate.active ? num(citrate.rate) : 0;
  const yinRateM2 = num(m2.yin_rate);
  const totalVol2_base = useMemo(() => num(m2.Qr_pre) + num(m2.Qr_post) + (m2.modality === 'CVVHDF' ? num(m2.Qd) : 0) + Qc2, [m2, Qc2]);
  const totalVol2 = useMemo(() => totalVol2_base + yinRateM2, [totalVol2_base, yinRateM2]);
  const na_pre2 = useMemo(() => num(m2.Qr_pre) * num(m2.Na_standard), [m2.Qr_pre, m2.Na_standard]);
  const na_post2 = useMemo(() => num(m2.Qr_post) * num(m2.Na_standard), [m2.Qr_post, m2.Na_standard]);
  const na_d2 = useMemo(() => m2.modality === 'CVVHDF' ? num(m2.Qd) * num(m2.Na_standard) : 0, [m2]);
  const na_c2 = useMemo(() => Qc2 * num(citrate.sodium), [Qc2, citrate.sodium]);
  const na_yin2 = useMemo(() => yinRateM2 * num(m2.Na_yin), [yinRateM2, m2.Na_yin]);
  const totalNa2_base = useMemo(() => na_pre2 + na_post2 + na_d2 + na_c2, [na_pre2, na_post2, na_d2, na_c2]);
  const totalNa2 = useMemo(() => totalNa2_base + na_yin2, [totalNa2_base, na_yin2]);
  const outletNa2 = useMemo(() => totalVol2 > 0 ? totalNa2 / totalVol2 : 0, [totalNa2, totalVol2]);
  const FF2 = useMemo(() => { const d = plasmaFlow + num(m2.Qr_pre) + Qc2; return d > 0 ? (num(m2.UF) + num(m2.Qr_pre) + num(m2.Qr_post) + yinRateM2 + Qc2) / d : 0; }, [m2, plasmaFlow, yinRateM2, Qc2]);
  const prescribedClearance2 = useMemo(() => {
    if (patient.mode === 'clearance') return bsa > 0 ? (totalVol2 / 1000) / (bsa / 1.73) : 0;
    return num(patient.weight) > 0 ? totalVol2 / num(patient.weight) : 0;
  }, [totalVol2, patient.mode, bsa, patient.weight]);
  const qRecommendM2 = useMemo(() => {
      const standardNa = num(m2.Na_standard);
      const vBase = num(m2.Qr_pre) + num(m2.Qr_post) + (m2.modality === 'CVVHDF' ? num(m2.Qd) : 0);
      const naBase = vBase * standardNa + Qc2 * num(citrate.sodium);
      const vTotal = vBase + Qc2;
      const denominator = num(m2.Na_yin) - num(patient.targetNa);
      return denominator !== 0 ? Math.max((num(patient.targetNa) * vTotal - naBase) / denominator, 0) : 0;
  }, [m2, patient.targetNa, Qc2, citrate.sodium]);


  // Method 3 Calcs
  const naBagRequired = useMemo(() => {
    if (!m3.includeCitrate || !citrate.active) return 0;

    const TargetNa = num(patient.targetNa);
   
    // Use the total target clearance (effluent) as V_crrt
    const V_crrt_mL = effluent;
    if (V_crrt_mL === 0) return 0; // Avoid division by zero
    const V_citrate_mL = num(citrate.rate);

    // Sodium concentration in mmol/L
    const Na_citrate_conc = num(citrate.sodium);

    // Total sodium from citrate in mmol/hr
    const Na_citrate_amount = (V_citrate_mL / 1000) * Na_citrate_conc;

    // Numerator and Denominator using volumes in L/hr to keep units consistent
    const numerator = (TargetNa * ((V_crrt_mL + V_citrate_mL) / 1000)) - Na_citrate_amount;
    const denominator = V_crrt_mL / 1000;

    const result = denominator > 0 ? numerator / denominator : 0;
    return result;
  }, [m3.includeCitrate, patient.targetNa, citrate, effluent]);

  const effectiveTargetNa = m3.includeCitrate && citrate.active ? naBagRequired : num(patient.targetNa);

  const m3WaterCalculation = useMemo(() => {
    const V0 = num(m3.bagVolume);
    const C0 = num(m3.bagNa);
    const T = effectiveTargetNa;

    if (T > C0) {
        return {
            type: 'add_sodium',
            sodiumToAdd: (T - C0) * (V0 / 1000)
        };
    }
   
    if (T <= 0) return { type: 'error', message: 'Target Na cannot be zero.'};

    const initialWaterToAdd = V0 * (C0 / T - 1);

    if (V0 + initialWaterToAdd <= 6000) {
        return {
            type: 'simple_add',
            waterToAdd: initialWaterToAdd,
            finalVolume: V0 + initialWaterToAdd,
            finalNa: T
        };
    }

    for (let removedLiters = 1; (removedLiters * 1000) < V0; removedLiters++) {
        const volumeRemoved = removedLiters * 1000;
        const volumeRemaining = V0 - volumeRemoved;
        const sodiumRemaining = C0 * (volumeRemaining / 1000);
        const finalVolumeNeededInMl = (sodiumRemaining / T) * 1000;
       
        if (finalVolumeNeededInMl <= 6000) {
            const waterToAddBack = finalVolumeNeededInMl - volumeRemaining;
            return {
                type: 'remove_add',
                volumeToRemove: volumeRemoved,
                waterToAdd: waterToAddBack,
                finalVolume: finalVolumeNeededInMl,
                finalNa: T,
                volumeRemaining: volumeRemaining,
                sodiumRemaining: sodiumRemaining,
                finalVolumeNeeded: finalVolumeNeededInMl
            };
        }
    }
   
    return {
        type: 'error',
        message: 'Cannot achieve target Na within 6L bag limit.'
    };
  }, [m3.bagVolume, m3.bagNa, effectiveTargetNa]);

  const outletNa3 = useMemo(() => {
    if (!m3.includeCitrate || !citrate.active) {
        return num(patient.targetNa);
    }
    const V_crrt_mL = effluent;
    const V_citrate_mL = num(citrate.rate);
    const totalVol_mL = V_crrt_mL + V_citrate_mL;

    const na_crrt_mmol = (V_crrt_mL / 1000) * naBagRequired;
    const na_citrate_mmol = (V_citrate_mL / 1000) * num(citrate.sodium);
    const totalNa_mmol = na_crrt_mmol + na_citrate_mmol;

    return totalVol_mL > 0 ? totalNa_mmol / (totalVol_mL / 1000) : 0;
  }, [m3.includeCitrate, citrate, effluent, patient.targetNa, naBagRequired]);


  const targetClearanceValue = patient.mode === 'clearance' ? num(patient.clearance) : num(patient.dose);
  const targetClearanceUnits = patient.mode === 'clearance' ? 'L/1.73m²/hr' : 'mL/kg/hr';
 
  // --- JSX RENDER ---
  const getOutletNaClass = (outletNa, targetNa) => {
    const deviation = Math.abs(outletNa - targetNa);
    if (deviation > 0.9) return "font-bold text-red-600";
    if (deviation > 0.5) return "font-bold text-orange-500";
    return "text-gray-800";
  };
 
  return (
    <div className="bg-[#F9F7FB] min-h-screen font-sans flex flex-col">
       {showDisclaimer && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl p-6 max-w-lg w-full mx-4">
            <h3 className="text-xl font-bold text-gray-800">Disclaimer & Important Considerations</h3>
            <div className="mt-4 text-sm text-gray-600 space-y-3">
                <p><strong>Clinical Simulation:</strong> This is a clinical simulation tool for comparing CRRT strategies in dysnatremia. It is <strong>not</strong> a substitute for expert clinical judgment and must not be used for direct patient care.</p>
                <p><strong>Clinical Judgment is Key:</strong> The formulas provided cannot, in isolation, guarantee a desired sodium correction. There are often multiple valid approaches, and the optimal choice requires clinician judgment based on the individual patient's context.</p>
                <p><strong>Citrate Anticoagulation:</strong> The use of citrate-based regional anticoagulation can significantly complicate the management of dysnatremia due to its sodium load. While this calculator provides modeling for citrate, it is <strong>strongly advised to consider alternative anticoagulants</strong> if feasible when precise sodium control is a primary goal.</p>
            </div>
            <div className="mt-6 text-right">
                 <button
                    onClick={() => setShowDisclaimer(false)}
                    className="w-full sm:w-auto text-white bg-[#4F2683] hover:bg-opacity-90 font-bold py-2 px-6 rounded-lg transition-colors duration-200"
                  >
                   I Understand and Agree
                  </button>
            </div>
          </div>
        </div>
       )}
       {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl p-6 max-w-lg w-full mx-4">
                <div className="flex justify-between items-start">
                    <h3 className="text-xl font-bold text-gray-800">How to Use This Calculator</h3>
                    <button onClick={() => setShowInfoModal(false)} className="p-1 text-gray-400 hover:text-gray-700 font-bold text-2xl leading-none">&times;</button>
                </div>
                <div className="mt-4 text-sm text-gray-600 space-y-4">
                    <div>
                        <h4 className="font-semibold text-gray-700">Step 1: Enter Patient & Target Parameters</h4>
                        <ul className="list-disc list-inside space-y-1 pl-2 mt-1">
                            <li>Begin by filling in the <strong>Patient & Clearance Parameters</strong> section.</li>
                            <li>Enter the patient's weight, height, Hct, and current serum sodium. The calculator will suggest a target sodium, which you can adjust.</li>
                            <li>Set the desired <strong>Target Clearance</strong> and adjust global settings like <strong>Citrate Anticoagulation</strong>, <strong>Minimum Post-Filter Rate</strong>, and your desired <strong>Target Filtration Fraction</strong>.</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-700">Step 2: Explore & Calculate Prescriptions</h4>
                        <ul className="list-disc list-inside space-y-1 pl-2 mt-1">
                            <li>For <strong>Method 1</strong> and <strong>Method 2</strong>, you can manually enter rates or click <strong>"Auto-adjust to Target Na"</strong>. This function aims to generate a viable strategy by calculating the fluid rates required to match the "Resulting Outlet Na⁺" to your "Target Na".</li>
                        </ul>
                    </div>
                     <div>
                        <h4 className="font-semibold text-gray-700">Step 3: Interpret the Results & Apply Clinical Judgment</h4>
                        <ul className="list-disc list-inside space-y-1 pl-2 mt-1">
                            <li>Review the outcomes for each method. It is crucial to understand that:
                                <ul className="list-disc list-inside pl-4 mt-1">
                                    <li>Some patient parameters may result in <strong>multiple possible prescriptions</strong> that meet the sodium target.</li>
                                    <li>Conversely, certain parameters may yield <strong>no single "optimal" prescription</strong> that satisfies all constraints (e.g., Target Clearance and a safe Filtration Fraction) simultaneously.</li>
                                </ul>
                            </li>
                            <li>Pay close attention to the warnings and gauges for <strong>Resulting Outlet Na⁺</strong>, <strong>Filtration Fraction (FF)</strong>, and <strong>Prescribed Clearance</strong> to understand the trade-offs.</li>
                            <li>The tool's purpose is to provide viable strategies to achieve a target outlet sodium. <strong>The results should never override your clinical judgment.</strong></li>
                        </ul>
                    </div>
                </div>
                 <div className="mt-6 text-right">
                     <button
                        onClick={() => setShowInfoModal(false)}
                        className="w-full sm:w-auto text-white bg-[#4F2683] hover:bg-opacity-90 font-bold py-2 px-6 rounded-lg transition-colors duration-200"
                      >
                       Close
                      </button>
                </div>
            </div>
        </div>
    )}
    {methodInfo.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl p-6 max-w-lg w-full mx-4">
                <div className="flex justify-between items-start">
                    <h3 className="text-xl font-bold text-gray-800">{methodInfo.title}</h3>
                    <button onClick={() => setMethodInfo({ isOpen: false, title: '', content: [] })} className="p-1 text-gray-400 hover:text-gray-700 font-bold text-2xl leading-none">&times;</button>
                </div>
                <div className="mt-4 text-sm text-gray-600 space-y-4">
                    {methodInfo.content.map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                    ))}
                </div>
                <div className="mt-6 text-right">
                    <button
                        onClick={() => setMethodInfo({ isOpen: false, title: '', content: [] })}
                        className="w-full sm:w-auto text-white bg-[#4F2683] hover:bg-opacity-90 font-bold py-2 px-6 rounded-lg transition-colors duration-200"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    )}
       {showM1ConflictModal && m1ConflictData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-800">Optimization Conflict</h3>
            <p className="mt-2 text-sm text-gray-600">
              Optimizing for target clearance results in a high Filtration Fraction of <strong className="text-red-600">{(m1ConflictData.ideal.FF * 100).toFixed(0)}%</strong>.
              Please choose a priority:
            </p>
            <div className="mt-4 space-y-3">
              <button
                onClick={() => {
                  setM1(p => ({ ...p, ...m1ConflictData.ideal }));
                  setShowM1ConflictModal(false);
                }}
                className="w-full text-white bg-[#4F2683] hover:bg-opacity-90 font-bold py-2 px-4 rounded-lg transition-colors duration-200 text-left"
              >
                <span className="font-bold block">Prioritize Target Clearance</span>
                <span className="text-xs font-normal">Accepts FF of {(m1ConflictData.ideal.FF * 100).toFixed(0)}%</span>
              </button>
              <button
                onClick={() => {
                  setM1(p => ({ ...p, ...m1ConflictData.safe }));
                  setShowM1ConflictModal(false);
                }}
                className="w-full text-gray-700 bg-gray-200 hover:bg-gray-300 font-bold py-2 px-4 rounded-lg transition-colors duration-200 text-left"
              >
                <span className="font-bold block">Prioritize Safe FF (&lt;{m1ConflictData.targetFF}%)</span>
                <span className="text-xs font-normal">Reduces clearance to ~{(m1ConflictData.safe.V_total_final).toFixed(0)} mL/hr</span>
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex-grow">
        <div className="container mx-auto p-4 sm:p-6 lg:p-8">
          <header className="relative text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-[#4F2683]">CRRT Dysnatremia Calculator</h1>
            <p className="text-lg text-gray-500 mt-3">Visually compare methods for managing sodium in CRRT.</p>
             <button
                onClick={() => setShowInfoModal(true)}
                className="absolute top-0 right-0 p-2 text-red-500 hover:text-red-700 transition-colors"
                aria-label="How to use this calculator"
            >
                <InfoIcon />
            </button>
          </header>

          {/* Patient Information Section */}
          <section className="p-6 bg-white rounded-xl shadow-lg border-t-4 border-[#4F2683] mb-16">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Patient & Clearance Parameters</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6">
              {/* Patient Inputs */}
              <InputField label="Weight" unit="kg" name="weight" value={patient.weight} onChange={handlePatient}/>
              <InputField label="Height" unit="cm" name="height" value={patient.height} onChange={handlePatient}/>
              <InputField label="Hct" unit="%" name="Hct" value={patient.Hct} onChange={handlePatient}/>
              <InputField label="BSA" unit="m²" name="bsa" value={bsa.toFixed(2)} disabled/>
              <InputField 
                label="Serum Na" 
                unit="mmol/L" 
                name="Na_patient" 
                value={patient.Na_patient} 
                onChange={handlePatient}
                labelClassName="font-bold"
                inputClassName="bg-yellow-50"
              />
              <InputField label="Blood Flow Qb" unit="mL/min" name="Qb" value={patient.Qb} onChange={handlePatient}/>
              <InputField 
                label="Target Na" 
                unit="mmol/L" 
                name="targetNa" 
                value={patient.targetNa} 
                onChange={handlePatient}
                labelClassName="font-bold"
                inputClassName="bg-yellow-50"
              />
             
              {/* Clearance Toggle and Input */}
              <div className="col-span-2 md:col-span-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-end mt-4 pt-4 border-t border-gray-200">
                  <div>
                      <label htmlFor="clearance-toggle" className="block text-sm font-medium text-gray-600">Target Clearance Unit</label>
                      <div id="clearance-toggle" className="mt-1 flex rounded-md shadow-sm">
                          <button type="button" onClick={() => setPatient(p => ({ ...p, mode: 'clearance' }))} className={`relative inline-flex items-center px-4 py-2 rounded-l-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-[#4F2683] focus:border-[#4F2683] transition-colors duration-150 ${patient.mode === 'clearance' ? 'bg-[#4F2683] text-white border-[#4F2683]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>L/1.73m²/hr</button>
                          <button type="button" onClick={() => setPatient(p => ({ ...p, mode: 'dose' }))} className={`-ml-px relative inline-flex items-center px-4 py-2 rounded-r-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-[#4F2683] focus:border-[#4F2683] transition-colors duration-150 ${patient.mode === 'dose' ? 'bg-[#4F2683] text-white border-[#4F2683]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>mL/kg/hr</button>
                      </div>
                  </div>
                  {patient.mode === 'clearance' 
                      ? <InputField label="Target Clearance" unit="L/hr/1.73m²" name="clearance" value={patient.clearance} onChange={handlePatient} step="0.1" />
                      : <InputField label="Target Dose" unit="mL/kg/hr" name="dose" value={patient.dose} onChange={handlePatient} />
                  }
              </div>
              <div className="col-span-2 md:col-span-4 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4 items-end mt-4 pt-4 border-t border-gray-200">
                <div className="pt-5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Citrate Anticoagulation?</span>
                    <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold transition-colors ${!citrate.active ? 'text-gray-800' : 'text-gray-400'}`}>No</span>
                        <ToggleSwitch isEnabled={citrate.active} onToggle={() => setCitrate(p => ({...p, active: !p.active}))} />
                        <span className={`text-sm font-semibold transition-colors ${citrate.active ? 'text-gray-800' : 'text-gray-400'}`}>Yes</span>
                    </div>
                  </div>
                   {citrate.active && <p className="text-xs text-gray-500 mt-2 italic">Note: Citrate may contribute significant sodium and fluctuating dosing can complicate dysnatremia management.</p>}
                </div>
                {citrate.active && <>
                  <InputField label="Citrate Rate" unit="mL/hr" name="rate" value={citrate.rate} onChange={handleCitrate} />
                  <InputField label="Citrate Na⁺" unit="mmol/L" name="sodium" value={citrate.sodium} onChange={handleCitrate} />
                </>}
              </div>
               <div className="col-span-2 md:col-span-4 grid grid-cols-1 sm:grid-cols-4 gap-x-6 gap-y-4 items-end mt-4 pt-4 border-t border-gray-200">
                <div className="pt-5 col-span-2">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">Minimal Post-Filter Rate?</span>
                        <div className="flex items-center gap-2">
                            <span className={`text-sm font-semibold transition-colors ${!minimalPFR.active ? 'text-gray-800' : 'text-gray-400'}`}>No</span>
                            <ToggleSwitch isEnabled={minimalPFR.active} onToggle={() => setMinimalPFR(p => ({...p, active: !p.active}))} />
                            <span className={`text-sm font-semibold transition-colors ${minimalPFR.active ? 'text-gray-800' : 'text-gray-400'}`}>Yes</span>
                        </div>
                    </div>
                </div>
                {minimalPFR.active && (
                    <InputField label="Minimal Post-Filter Rate" unit="mL/hr" name="rate" value={minimalPFR.rate} onChange={handleMinimalPFR} />
                )}
                <div className="sm:col-start-4">
                    <InputField label="Target Filtration Fraction" unit="%" value={targetFF} onChange={handleTargetFF} />
                </div>
              </div>
            </div>
          </section>

          {/* Methods Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {/* Method 1 Card */}
              <div className="bg-indigo-50 rounded-xl shadow-xl p-6 flex flex-col text-indigo-900">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-white flex items-center justify-center"><FluidBagIcon className="h-6 w-6 text-indigo-600"/></div>
                        <div>
                        <h3 className="text-lg font-bold">Method 1: Change Post-Filter Fluid</h3>
                        <p className="text-sm text-indigo-800 opacity-90 font-normal">Use a hypo- or hypertonic post-filter replacement solution.</p>
                        </div>
                    </div>
                    <button 
                        onClick={() => handleShowMethodInfo('About Method 1', [
                            "This method involves using standard CRRT solutions for the pre-blood pump (pre-filter) replacement fluid and dialysate (if applicable) and then using a different standard IV solution for the post-filter replacement fluid to alter the outlet sodium concentration of the entire circuit.",
                            "Note that this method may not always be optimal for very small patients, nor for patients with mild hyponatremia or with hypernatremia - unless alterations are made in the minimal post-filter replacement rate, filtration fraction threshold, and/or overall clearance."
                        ])}
                        className="p-1 text-red-500 hover:text-red-700 transition-colors"
                        aria-label="About Method 1"
                    >
                        <InfoIcon className="w-5 h-5"/>
                    </button>
                  </div>
                 
                  <div className="mb-4 bg-indigo-100 p-2 rounded-lg">
                    <ToggleSwitch label="Auto-adjust to Target Na" isEnabled={isM1Optimized} onToggle={() => setIsM1Optimized(prev => !prev)} />
                     {isM1Optimized && <p className="text-xs text-indigo-800 opacity-90 mt-2 italic">This is one possible combination to achieve the target. Adjust fluids and rates as needed based on clinical judgment.</p>}
                  </div>

                  <div className="space-y-4 pt-4 border-t border-indigo-200">
                      <label className="block"><span className="text-xs font-semibold text-gray-500 uppercase">Modality</span><select name="modality" value={m1.modality} onChange={handleM1} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"><option value="CVVHDF">CVVHDF</option><option value="CVVH">CVVH</option></select></label>
                      <div className="grid grid-cols-2 gap-4">
                          <InputField label="Pre-filter" unit="ml/hr" name="Qr_pre" value={m1.Qr_pre} onChange={handleM1} />
                          <InputField label="Post-filter" unit="ml/hr" name="Qr_post" value={m1.Qr_post} onChange={handleM1} />
                          {m1.modality==='CVVHDF' && <InputField label="Dialysate" unit="ml/hr" name="Qd" value={m1.Qd} onChange={handleM1} />}
                          <InputField label="Net fluid removal (positive value is removed)" unit="ml/hr" name="UF" value={m1.UF} onChange={handleM1} />
                          <InputField label="Standard CRRT Na⁺" unit="mmol/L" name="Na_standard" value={m1.Na_standard} onChange={handleM1} />
                          <div className="col-span-2">
                            <label className="block text-xs font-semibold text-gray-500 uppercase">Post-filter Na⁺</label>
                            <select name="Na_r_post" value={m1.Na_r_post} onChange={handleM1} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm">
                              <option value={140}>140 (Standard)</option>
                              <option value={154}>154 (0.9% NS)</option>
                              <option value={77}>77 (0.45% NS)</option>
                              <option value={34}>34 (0.2% NS)</option>
                              <option value={513}>513 (3% HTS)</option>
                              <option value={0}>0 (D5W or Sterile Water)</option>
                            </select>
                            {num(m1.Na_r_post) === 0 && <p className="text-xs text-orange-600 mt-2">Note: D5W contains glucose (~50 g/L); sterile water does not. At higher rates, D5W can affect blood glucose, so use clinical judgment and monitor glucose.</p>}
                          </div>
                      </div>
                  </div>
                  {m1Error && <div className="mt-4 p-2 text-xs text-red-700 bg-red-100 rounded-md">{m1Error}</div>}
                  <div className="mt-6 p-4 bg-white bg-opacity-60 rounded-lg space-y-3">
                      <div className="flex justify-between items-center"><span className="text-sm font-semibold">Resulting Outlet Na⁺</span><span className={`text-xl font-bold ${getOutletNaClass(outletNa1, num(patient.targetNa))}`}>{outletNa1.toFixed(1)} <span className="text-sm font-normal text-gray-500">mmol/L</span>{Math.abs(outletNa1 - num(patient.targetNa)) > 0.5 && <WarningIcon/>}</span></div>
                      {Math.abs(outletNa1 - num(patient.targetNa)) > 0.5 && <div className="text-xs text-orange-600 text-right -mt-2">Outlet sodium is NOT at desired target</div>}
                      <div className="flex justify-between items-center"><span className="text-sm font-semibold">Prescribed Clearance</span><span className={`text-base font-semibold ${prescribedClearance1 > targetClearanceValue * 1.1 ? "text-red-600" : "text-indigo-900"}`}>{prescribedClearance1.toFixed(1)} <span className="text-sm font-normal text-gray-500">{targetClearanceUnits}</span></span></div>
                      <div className="flex justify-between items-center pt-3 border-t"><span className="text-sm font-semibold">Filtration Fraction</span><Gauge value={FF1} /></div>
                  </div>
                  <Collapsible title="Show Calculations" isOpen={showCalc1} onToggle={() => setShowCalc1(!showCalc1)}>
                      <div className="p-3 bg-white bg-opacity-60 rounded border text-xs text-gray-600 space-y-2">
                          <div>
                              <p className="font-semibold underline">Total Sodium (mmol/hr):</p>
                              <p>Na from Pre-filter: <span className="font-mono">{(na_pre1/1000).toFixed(4)}</span></p>
                              <p>Na from Post-filter: <span className="font-mono">{(na_post1/1000).toFixed(4)}</span></p>
                              {m1.modality === 'CVVHDF' && <>
                                  <p>Na from Dialysate: <span className="font-mono">{(na_d1/1000).toFixed(4)}</span></p>
                              </>}
                              {citrate.active && <p>Na from Citrate: <span className="font-mono">{(na_c1/1000).toFixed(4)}</span></p>}
                              <p className="border-t border-gray-300 mt-1 pt-1"><strong>Total Na In:</strong> <span className="font-mono">{(totalNa1/1000).toFixed(4)}</span></p>
                          </div>
                          <div>
                              <p className="font-semibold underline">Total Volume (L/hr):</p>
                              <p><strong>Total Vol In:</strong> <span className="font-mono">{(totalVol1/1000).toFixed(4)}</span></p>
                          </div>
                          <div className="pt-1 border-t border-gray-300">
                              <p className="font-semibold">Outlet Na = Total Na / Total Vol</p>
                              <p className="font-mono">{(totalNa1/1000).toFixed(4)} / {(totalVol1/1000).toFixed(4)} = <strong>{outletNa1.toFixed(1)} mmol/L</strong></p>
                          </div>
                          <div className="pt-2 mt-2 border-t border-gray-300">
                              <p className="font-semibold">Filtration Fraction = (UF + Qr_pre + Qr_post + Qc) / (Plasma Flow + Qr_pre + Qc)</p>
                              <p className="font-mono">({num(m1.UF)} + {num(m1.Qr_pre)} + {num(m1.Qr_post)} + {Qc1}) / ({plasmaFlow.toFixed(0)} + {num(m1.Qr_pre)} + {Qc1}) = <strong>{FF1.toFixed(2)}</strong></p>
                          </div>
                           <div className="pt-2 mt-2 border-t border-gray-300">
                              <p className="font-semibold">Prescribed Clearance</p>
                              {patient.mode === 'clearance' ? 
                                <p className="font-mono">(Total Vol / 1000) / (BSA / 1.73) = <strong>{prescribedClearance1.toFixed(1)}</strong> L/hr/1.73m²</p>
                                :
                                <p className="font-mono">Total Vol / Weight = <strong>{prescribedClearance1.toFixed(1)}</strong> mL/kg/hr</p>
                              }
                          </div>
                      </div>
                  </Collapsible>
                  <Collapsible title="Show Schematic" isOpen={showSchematic1} onToggle={() => setShowSchematic1(!showSchematic1)}>
                      <BaseSchematic highlight="post" />
                  </Collapsible>
              </div>

              {/* Method 2 Card */}
              <div className="bg-amber-50 rounded-xl shadow-xl p-6 flex flex-col text-amber-900">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-white flex items-center justify-center"><FluidBagIcon className="h-6 w-6 text-amber-600"/></div>
                        <div>
                        <h3 className="text-lg font-bold">Method 2: Run Continuous IV Infusion</h3>
                        <p className="text-sm text-amber-800 opacity-90 font-normal">Run a separate continuous IV infusion (and remove added fluid with CRRT).</p>
                        </div>
                    </div>
                     <button 
                        onClick={() => handleShowMethodInfo('About Method 2', [
                            "This method involves using standard CRRT solutions for the pre-blood pump (pre-filter) and post-filter replacement fluid, and dialysate (if applicable). A separate IV infusion (of a hypotonic or hypertonic fluid as indicated) is then run on the return line, or directly to the patient to control the overall hourly outlet sodium delivery.",
                            "Note that for this method it is assumed that any volume added with the separate infusion is being removed with CRRT (i.e. it acts as an additional replacement fluid)."
                        ])}
                        className="p-1 text-red-500 hover:text-red-700 transition-colors"
                        aria-label="About Method 2"
                    >
                        <InfoIcon className="w-5 h-5"/>
                    </button>
                  </div>
                 
                  <div className="mb-4 bg-amber-100 p-2 rounded-lg">
                    <ToggleSwitch label="Auto-adjust to Target Na" isEnabled={isM2Optimized} onToggle={() => setIsM2Optimized(prev => !prev)} />
                    {isM2Optimized && <p className="text-xs text-amber-800 opacity-90 mt-2 italic">This is one possible combination to achieve the target. Adjust fluids and rates as needed based on clinical judgment.</p>}
                  </div>

                  <div className="space-y-4 pt-4 border-t border-amber-200">
                      <label className="block"><span className="text-xs font-semibold text-gray-500 uppercase">Modality</span><select name="modality" value={m2.modality} onChange={handleM2} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"><option value="CVVHDF">CVVHDF</option><option value="CVVH">CVVH</option></select></label>
                      <div className="grid grid-cols-2 gap-4">
                          <InputField label="Pre-filter" unit="ml/hr" name="Qr_pre" value={m2.Qr_pre} onChange={handleM2} />
                          <InputField label="Post-filter" unit="ml/hr" name="Qr_post" value={m2.Qr_post} onChange={handleM2} />
                          {m2.modality==='CVVHDF' && <InputField label="Dialysate" unit="ml/hr" name="Qd" value={m2.Qd} onChange={handleM2} /> }
                          <InputField label="Net fluid removal (positive value is removed)" unit="ml/hr" name="UF" value={m2.UF} onChange={handleM2} />
                          <InputField label="Standard CRRT Na⁺" unit="mmol/L" name="Na_standard" value={m2.Na_standard} onChange={handleM2} />
                          <div className="col-span-2">
                            <label className="block text-xs font-semibold text-gray-500 uppercase">IV Infusion Na⁺</label>
                            <select name="Na_yin" value={m2.Na_yin} onChange={handleM2} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"><option value={0}>D5W or Sterile Water (0)</option><option value={34}>1/4 NS (34)</option><option value={77}>1/2 NS (77)</option><option value={154}>NS (154)</option><option value={513}>3% Saline (513)</option></select>
                            {num(m2.Na_yin) === 0 && <p className="text-xs text-orange-600 mt-2">Note: D5W contains glucose (~50 g/L); sterile water does not. At higher rates, D5W can affect blood glucose, so use clinical judgment and monitor glucose.</p>}
                          </div>
                      </div>
                      <InputField label="IV Infusion Rate" unit="mL/hr" name="yin_rate" value={m2.yin_rate} onChange={handleM2} />
                  </div>
                  <div className="mt-6 p-4 bg-white bg-opacity-60 rounded-lg space-y-3">
                      <div className="flex justify-between items-center"><span className="text-sm font-semibold">Resulting Outlet Na⁺</span><span className={`text-xl font-bold ${getOutletNaClass(outletNa2, num(patient.targetNa))}`}>{outletNa2.toFixed(1)} <span className="text-sm font-normal text-gray-500">mmol/L</span>{Math.abs(outletNa2 - num(patient.targetNa)) > 0.5 && <WarningIcon/>}</span></div>
                      {Math.abs(outletNa2 - num(patient.targetNa)) > 0.5 && <div className="text-xs text-orange-600 text-right -mt-2">Outlet sodium is NOT at desired target</div>}
                      <div className="flex justify-between items-center"><span className="text-sm font-semibold">IV Infusion Rate Needed</span><span className="text-base font-semibold">{qRecommendM2.toFixed(0)} <span className="text-sm font-normal text-gray-500">mL/hr</span></span></div>
                      <div className="flex justify-between items-center"><span className="text-sm font-semibold">Prescribed Clearance</span><span className={`text-base font-semibold ${prescribedClearance2 > targetClearanceValue * 1.1 ? "text-red-600" : "text-amber-900"}`}>{prescribedClearance2.toFixed(1)} <span className="text-sm font-normal text-gray-500">{targetClearanceUnits}</span></span></div>
                      <div className="flex justify-between items-center pt-3 border-t"><span className="text-sm font-semibold">Filtration Fraction</span><Gauge value={FF2} /></div>
                  </div>
                  <Collapsible title="Show Calculations" isOpen={showCalc2} onToggle={() => setShowCalc2(!showCalc2)}>
                      <div className="p-3 bg-white bg-opacity-60 rounded border text-xs text-gray-600 space-y-2">
                           <div>
                              <p className="font-semibold underline">Total Sodium (mmol/hr):</p>
                              <p>Na from CRRT fluids: <span className="font-mono">{((na_pre2 + na_post2 + na_d2)/1000).toFixed(4)}</span></p>
                              {citrate.active && <p>Na from Citrate: <span className="font-mono">{(na_c2/1000).toFixed(4)}</span></p>}
                              <p>Na from IV Infusion: <span className="font-mono">{(na_yin2/1000).toFixed(4)}</span></p>
                              <p className="border-t border-gray-300 mt-1 pt-1"><strong>Total Na In:</strong> <span className="font-mono">{(totalNa2/1000).toFixed(4)}</span></p>
                          </div>
                          <div>
                              <p className="font-semibold underline">Total Volume (L/hr):</p>
                              <p>Vol from CRRT fluids: <span className="font-mono">{(totalVol2_base/1000).toFixed(4)}</span></p>
                              <p>Vol from IV Infusion: <span className="font-mono">{(yinRateM2/1000).toFixed(4)}</span></p>
                              <p className="border-t border-gray-300 mt-1 pt-1"><strong>Total Vol In:</strong> <span className="font-mono">{(totalVol2/1000).toFixed(4)}</span></p>
                          </div>
                          <div className="pt-1 border-t border-gray-300">
                              <p className="font-semibold">Outlet Na = Total Na / Total Vol</p>
                              <p className="font-mono">{(totalNa2/1000).toFixed(4)} / {(totalVol2/1000).toFixed(4)} = <strong>{outletNa2.toFixed(1)} mmol/L</strong></p>
                          </div>
                           <div className="pt-2 mt-2 border-t border-gray-300">
                              <p className="font-semibold">Filtration Fraction = (UF + Qr_pre + Qr_post + Qc + Q_IV) / (Plasma Flow + Qr_pre + Qc)</p>
                              <p className="font-mono">({num(m2.UF)} + {num(m2.Qr_pre)} + {num(m2.Qr_post)} + {Qc2} + {yinRateM2}) / ({plasmaFlow.toFixed(0)} + {num(m2.Qr_pre)} + {Qc2}) = <strong>{FF2.toFixed(2)}</strong></p>
                          </div>
                           <div className="pt-2 mt-2 border-t border-gray-300">
                              <p className="font-semibold">Prescribed Clearance</p>
                              {patient.mode === 'clearance' ? 
                                <p className="font-mono">(Total Vol / 1000) / (BSA / 1.73) = <strong>{prescribedClearance2.toFixed(1)}</strong> L/hr/1.73m²</p>
                                :
                                <p className="font-mono">Total Vol / Weight = <strong>{prescribedClearance2.toFixed(1)}</strong> mL/kg/hr</p>
                              }
                          </div>
                      </div>
                  </Collapsible>
                  <Collapsible title="Show Schematic" isOpen={showSchematic2} onToggle={() => setShowSchematic2(!showSchematic2)}>
                      <BaseSchematic highlight="yin" showYin={true} />
                  </Collapsible>
              </div>

              {/* Method 3 Card */}
              <div className="bg-teal-50 rounded-xl shadow-xl p-6 flex flex-col text-teal-900">
                   <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-white flex items-center justify-center"><BeakerIcon className="h-6 w-6 text-teal-600"/></div>
                            <div>
                            <h3 className="text-lg font-bold">Method 3: Modify Fluid Bag</h3>
                            <p className="text-sm text-teal-800 opacity-90 font-normal">Add water or Na⁺ directly to the CRRT solution bags.</p>
                            </div>
                        </div>
                        <button 
                            onClick={() => handleShowMethodInfo('About Method 3', [
                                "This method involves adding free water or sodium (as indicated) to standard CRRT fluid bags to achieve a desired CRRT outlet sodium concentration. It is assumed that adjustments are being made to all CRRT bags (replacement fluids and dialysate).",
                                "It is assumed that if a volume above 1L is to be ADDED to a CRRT solution, that some volume may need to be removed first. This is included where applicable in the calculations."
                            ])}
                            className="p-1 text-red-500 hover:text-red-700 transition-colors"
                            aria-label="About Method 3"
                        >
                            <InfoIcon className="w-5 h-5"/>
                        </button>
                    </div>
                  <div className="space-y-4 pt-4 border-t border-teal-200">
                      <InputField label="Bag Volume" unit="mL" name="bagVolume" value={m3.bagVolume} onChange={handleM3} />
                      <InputField label="Bag Na⁺" unit="mmol/L" name="bagNa" value={m3.bagNa} onChange={handleM3} />
                      <div className="pt-4 mt-4 border-t border-teal-200">
                        <ToggleSwitch 
                            label="Include Citrate in Calculation"
                            isEnabled={m3.includeCitrate} 
                            onToggle={() => setM3(p => ({...p, includeCitrate: !p.includeCitrate}))} 
                        />
                       {!citrate.active && m3.includeCitrate && <p className="text-xs text-orange-600 mt-2">Citrate AC must be enabled in the main patient parameters section for this calculation to work.</p>}
                      </div>
                  </div>
                  <div className="mt-6 p-4 bg-white bg-opacity-60 rounded-lg text-sm space-y-3">
                      {m3.includeCitrate && citrate.active && (
                          <div className="pb-3 border-b border-gray-300">
                              <p><strong>Required Bag Na⁺:</strong> <span className="text-lg font-bold">{naBagRequired.toFixed(1)}</span> mmol/L</p>
                              <p className="text-xs text-gray-600">This is the Na⁺ concentration CRRT bags need to achieve the target outlet Na⁺, accounting for citrate.</p>
                          </div>
                      )}
                      <div>
                        {m3WaterCalculation.type === 'add_sodium' && (
                            <p><strong>Sodium to Add:</strong> <span className="text-lg font-bold">{m3WaterCalculation.sodiumToAdd.toFixed(1)}</span> mmol to each bag</p>
                        )}
                        {m3WaterCalculation.type === 'simple_add' && (
                            <p><strong>Water to Add:</strong> <span className="text-lg font-bold">{m3WaterCalculation.waterToAdd.toFixed(0)}</span> mL to each bag</p>
                        )}
                        {m3WaterCalculation.type === 'remove_add' && (
                            <div>
                                <p><strong>Action:</strong> <span className="text-lg font-bold">Remove {m3WaterCalculation.volumeToRemove.toFixed(0)} mL</span> from bag, then <span className="text-lg font-bold">add {m3WaterCalculation.waterToAdd.toFixed(0)} mL</span> of sterile water.</p>
                                <p className="text-xs text-gray-600 mt-1">Final Bag: {m3WaterCalculation.finalVolume.toFixed(0)} mL at {m3WaterCalculation.finalNa.toFixed(1)} mmol/L</p>
                            </div>
                        )}
                        {m3WaterCalculation.type === 'error' && (
                            <p className="text-red-600 font-bold">{m3WaterCalculation.message}</p>
                        )}
                      </div>
                      <div className="pt-3 border-t">
                        <div className="flex justify-between items-center"><span className="text-sm font-semibold">Resulting Outlet Na⁺</span><span className={`text-xl font-bold ${getOutletNaClass(outletNa3, num(patient.targetNa))}`}>{outletNa3.toFixed(1)} <span className="text-sm font-normal text-gray-500">mmol/L</span>{Math.abs(outletNa3 - num(patient.targetNa)) > 0.5 && <WarningIcon/>}</span></div>
                        {Math.abs(outletNa3 - num(patient.targetNa)) > 0.5 && <div className="text-xs text-orange-600 text-right -mt-2">Outlet sodium is NOT at desired target</div>}
                      </div>
                  </div>
                  <Collapsible title="Show Calculations" isOpen={showCalc3} onToggle={() => setShowCalc3(!showCalc3)}>
                      <div className="p-3 bg-white bg-opacity-60 rounded border text-xs text-gray-600 break-words space-y-4">
                          {m3WaterCalculation.type === 'remove_add' ? (
                            <div>
                                <p className="font-semibold">1. Initial water to add exceeds 1L, recalculation needed.</p>

                                <p className="font-semibold mt-2">2. Recalculate after removing {m3WaterCalculation.volumeToRemove} mL:</p>
                                <p className="italic text-[11px]">Volume Remaining = Bag Volume - Volume Removed</p>
                                <p className="mt-1 font-mono text-[10px]">{m3.bagVolume} mL - {m3WaterCalculation.volumeToRemove} mL = <strong>{m3WaterCalculation.volumeRemaining.toFixed(0)} mL</strong></p>

                                <p className="italic text-[11px] mt-1">Sodium Remaining = Bag Na⁺ * Volume Remaining (L)</p>
                                <p className="mt-1 font-mono text-[10px]">{m3.bagNa} mmol/L * {(m3WaterCalculation.volumeRemaining / 1000).toFixed(1)} L = <strong>{m3WaterCalculation.sodiumRemaining.toFixed(0)} mmol</strong></p>

                                <p className="italic text-[11px] mt-1">Final Volume Needed = Sodium Remaining / Target Na⁺</p>
                                <p className="mt-1 font-mono text-[10px]">{m3WaterCalculation.sodiumRemaining.toFixed(0)} mmol / {effectiveTargetNa.toFixed(1)} mmol/L = <strong>{(m3WaterCalculation.finalVolumeNeeded / 1000).toFixed(2)} L</strong></p>

                                <p className="italic text-[11px] mt-1">Water to Add Back = Final Volume Needed - Volume Remaining</p>
                                <p className="mt-1 font-mono text-[10px]">{m3WaterCalculation.finalVolumeNeeded.toFixed(0)} mL - {m3WaterCalculation.volumeRemaining.toFixed(0)} mL = <strong>{m3WaterCalculation.waterToAdd.toFixed(0)} mL</strong></p>
                            </div>
                          ) : (
                            <>
                              {m3.includeCitrate && citrate.active ? (
                                <>
                                  <div>
                                    <p className="font-semibold underline">Total Sodium (mmol/hr):</p>
                                    <p>Na from CRRT fluids: <span className="font-mono">{((effluent / 1000) * naBagRequired).toFixed(1)}</span></p>
                                    <p>Na from Citrate: <span className="font-mono">{((num(citrate.rate) / 1000) * num(citrate.sodium)).toFixed(1)}</span></p>
                                    <p className="border-t border-gray-300 mt-1 pt-1"><strong>Total Na In:</strong> <span className="font-mono">{(((effluent / 1000) * naBagRequired) + ((num(citrate.rate) / 1000) * num(citrate.sodium))).toFixed(1)}</span></p>
                                  </div>
                                  <div>
                                    <p className="font-semibold underline">Total Volume (L/hr):</p>
                                    <p>Vol from CRRT fluids: <span className="font-mono">{(effluent / 1000).toFixed(2)}</span></p>
                                    <p>Vol from Citrate: <span className="font-mono">{(num(citrate.rate) / 1000).toFixed(2)}</span></p>
                                    <p className="border-t border-gray-300 mt-1 pt-1"><strong>Total Vol In:</strong> <span className="font-mono">{((effluent + num(citrate.rate)) / 1000).toFixed(2)}</span></p>
                                  </div>
                                  <div className="pt-2 border-t border-gray-300">
                                    <p className="font-semibold">1. Required Bag Na⁺ (Na_req)</p>
                                    <p className="italic text-[11px]">(TargetNa * (V_crrt + V_citrate) - Na_citrate) / V_crrt</p>
                                    <p className="mt-1 font-mono text-[10px]">({patient.targetNa} * (({effluent.toFixed(0)} + {num(citrate.rate)})/1000) - (({num(citrate.rate)}/1000)*{num(citrate.sodium)})) / ({effluent.toFixed(0)}/1000) = <strong>{naBagRequired.toFixed(1)} mmol/L</strong></p>
                                  </div>
                                </>
                              ) : (
                                <>
                                  {m3WaterCalculation.type === 'simple_add' && (<>
                                      <p className="font-semibold">Water = BagVol × (BagNa / TargetNa – 1)</p>
                                      <p className="mt-1 font-mono">{m3.bagVolume} × ({m3.bagNa} / {patient.targetNa} – 1) = <strong>{m3WaterCalculation.waterToAdd?.toFixed(0) || 'N/A'} mL</strong></p>
                                  </>)}
                                  {m3WaterCalculation.type === 'add_sodium' && (<>
                                      <p className="font-semibold">Sodium = (TargetNa – BagNa) × (BagVol/1000)</p>
                                      <p className="mt-1 font-mono">({patient.targetNa} – {m3.bagNa}) × ({m3.bagVolume}/1000) = <strong>{m3WaterCalculation.sodiumToAdd?.toFixed(1) || 'N/A'} mmol</strong></p>
                                  </>)}
                                </>
                              )}
                            </>
                          )}
                      </div>
                  </Collapsible>
                  <div className="flex-grow"></div> {/* Spacer */}
                  <Collapsible title="Show Schematic" isOpen={showSchematic3} onToggle={() => setShowSchematic3(!showSchematic3)}>
                      <BaseSchematic highlightAllBags={true} />
                  </Collapsible>
              </div>
          </div>
        </div>
      </div>
      <footer className="text-center p-4 mt-8 text-xs text-gray-500">
        <p>Created by Dr. Amrit Kirpalani MD, MEHP, FRCPC. For clinical modeling and simulation to help qualified medical professionals. Should NOT replace provider clinical decision-making. Licensed under CC BY-NC-SA 4.0.</p>
      </footer>
    </div>
  );
}